import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { GraduationCap, Plus, BookOpen, Layers, Clock } from "lucide-react";
import { useTutorial } from "@/lib/tutorial";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const { startIfNew } = useTutorial();
  useEffect(() => {
    const t = setTimeout(() => startIfNew("courses"), 900);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    api.get("/courses").then(r => {
      setCourses(r.data.courses || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] border border-[#B8A4FF]/20 flex items-center justify-center">
              <GraduationCap size={18} className="text-[#B8A4FF]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Courses</h1>
              <p className="text-[13px] text-[#B0AEBC]">Multi-study courses with unified flashcard decks & exams</p>
            </div>
          </div>
          <Link to="/app/courses/new" className="btn btn-primary px-4 py-2 text-[13px] flex items-center gap-1.5"
            data-testid="new-course-btn">
            <Plus size={14} /> New Course
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#B0AEBC]">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="text-center py-20">
            <GraduationCap size={48} className="text-[#4a4a5a] mx-auto mb-4" />
            <h2 className="text-[18px] font-semibold text-white mb-2">No courses yet</h2>
            <p className="text-[14px] text-[#B0AEBC] mb-6">
              Merge 2–5 of your studies into a course for a comprehensive learning experience.
            </p>
            <Link to="/app/courses/new" className="btn btn-primary px-6 py-3 text-[14px] inline-flex items-center gap-2">
              <Plus size={15} /> Create your first course
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {courses.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Link to={`/app/courses/${c.id}`} className="card p-5 hover:border-[#3a3a4e] block transition-colors"
                  data-testid={`course-card-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#B8A4FF]/20 to-[#4A90E2]/20 border border-[#B8A4FF]/15 flex items-center justify-center flex-shrink-0">
                      <GraduationCap size={18} className="text-[#B8A4FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-[15px] font-semibold text-white truncate">{c.title}</h3>
                      {c.description && (
                        <p className="text-[12px] text-[#B0AEBC] mt-0.5 truncate">{c.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[#B0AEBC]">
                        <span className="flex items-center gap-1"><BookOpen size={11} /> {c.study_ids?.length} studies</span>
                        <span className="flex items-center gap-1"><Layers size={11} /> {c.total_flashcards} cards</span>
                      </div>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {c.study_titles?.slice(0, 3).map((t, j) => (
                          <span key={j} className="text-[10px] bg-[#2a2a3e] text-[#B0AEBC] px-1.5 py-0.5 rounded">
                            {t.length > 20 ? t.slice(0, 20) + "…" : t}
                          </span>
                        ))}
                        {(c.study_titles?.length || 0) > 3 && (
                          <span className="text-[10px] text-[#6B6B7B]">+{c.study_titles.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
